/**
 * Footer component
 * - Text-based four-column footer: Useful Links, Company, Contact Information, Social Media
 * - Dark contrasting background and clear separation from main content
 * - Fully responsive (1/2/4 columns) and accessible
 */

import React from 'react'
import { Link } from 'react-router'
import {
  CheckCircle,
  MapPin,
  Mail,
  Phone,
  Facebook,
  Instagram,
  Linkedin,
} from 'lucide-react'

/** External link attributes for security */
const ext = {
  target: '_blank',
  rel: 'noopener noreferrer',
} as const

/** Single line item with a leading icon (used in lists) */
function LineItem({
  icon: Icon,
  children,
  href,
  to,
}: {
  icon: React.ComponentType<React.SVGProps<SVGSVGElement>>
  children: React.ReactNode
  href?: string
  to?: string
}): JSX.Element {
  /** Decide between anchor and router Link */
  const Content = (
    <span className="inline-flex items-start gap-2">
      <Icon className="h-4 w-4 mt-0.5 text-orange-500 flex-shrink-0" />
      <span className="leading-snug">{children}</span>
    </span>
  )

  if (to) {
    return (
      <Link
        to={to}
        className="text-slate-300 hover:text-white transition-colors"
        aria-label={typeof children === 'string' ? children : undefined}
      >
        {Content}
      </Link>
    )
  }

  if (href) {
    const external = href.startsWith('http')
    return (
      <a
        href={href}
        {...(external ? ext : {})}
        className="text-slate-300 hover:text-white transition-colors"
        aria-label={typeof children === 'string' ? children : undefined}
      >
        {Content}
      </a>
    )
  }

  return <div className="text-slate-300">{Content}</div>
}

/** Footer component rendered on every page */
export default function Footer(): JSX.Element {
  return (
    <footer className="bg-slate-900 text-slate-300 border-t border-slate-800/60">
      {/* Main footer content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        {/* 4-column responsive grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-10">
          {/* Useful Links */}
          <div>
            <h3 className="text-white font-semibold tracking-wide mb-4">
              Useful Links
            </h3>
            <ul className="space-y-3">
              <li>
                <LineItem icon={CheckCircle} to="/contact">
                  Contact Us
                </LineItem>
              </li>
              <li>
                <LineItem icon={CheckCircle} to="/services">
                  Services
                </LineItem>
              </li>
            </ul>
          </div>

          {/* Company */}
          <div>
            <h3 className="text-white font-semibold tracking-wide mb-4">
              Company
            </h3>
            <ul className="space-y-3">
              <li>
                <LineItem icon={CheckCircle} to="/about">
                  About
                </LineItem>
              </li>
              <li>
                {/* Link to dedicated Terms of Service page */}
                <LineItem icon={CheckCircle} to="/terms">
                  Terms of Service
                </LineItem>
              </li>
              <li>
                {/* Link to dedicated Privacy Policy page */}
                <LineItem icon={CheckCircle} to="/privacy">
                  Privacy Policy
                </LineItem>
              </li>
            </ul>
          </div>

          {/* Contact Information */}
          <div>
            <h3 className="text-white font-semibold tracking-wide mb-4">
              Contact Information
            </h3>
            <ul className="space-y-3">
              <li>
                <LineItem icon={MapPin} href="https://maps.app.goo.gl/pM1XFyftEGiE3Fru8">
                  <span>
                    Baharke Road, Next to Majesty School
                    <br />
                    Erbil, Iraq
                  </span>
                </LineItem>
              </li>
              <li>
                <LineItem icon={Mail} href="mailto:info@edicoo.com">
                  info@edicoo.com
                </LineItem>
              </li>
              <li>
                <LineItem icon={Phone} href="tel:+9647504132323">
                  +964 750 413 2323
                </LineItem>
              </li>
            </ul>
          </div>

          {/* Social Media */}
          <div>
            <h3 className="text-white font-semibold tracking-wide mb-4">
              Social Media
            </h3>
            <div className="flex items-center gap-3">
              <a
                href="https://www.facebook.com/edicooadvertising/"
                aria-label="Edicoo on Facebook"
                className="grid place-items-center h-8 w-8 rounded bg-orange-600 text-white hover:bg-orange-500 transition-colors"
                {...ext}
              >
                <Facebook className="h-4 w-4" />
              </a>

              <a
                href="https://www.instagram.com/edicoo.advertising"
                aria-label="Edicoo on Instagram"
                className="grid place-items-center h-8 w-8 rounded bg-orange-600 text-white hover:bg-orange-500 transition-colors"
                {...ext}
              >
                <Instagram className="h-4 w-4" />
              </a>
              <a
                href="https://www.linkedin.com/company/edicoo-advertising-agency"
                aria-label="Edicoo on LinkedIn"
                className="grid place-items-center h-8 w-8 rounded bg-orange-600 text-white hover:bg-orange-500 transition-colors"
                {...ext}
              >
                <Linkedin className="h-4 w-4" />
              </a>
            </div>
          </div>
        </div>

        {/* Divider and copyright */}
        <div className="mt-8 border-t border-white/10 pt-4 text-xs text-slate-500">
          © {new Date().getFullYear()} Edicoo Advertising Agency. All rights reserved.
        </div>
      </div>
    </footer>
  )
}
